import {
  Connection,
  PublicKey,
  Transaction,
  type TransactionInstruction,
} from '@solana/web3.js'
import type { WalletContextState } from '@solana/wallet-adapter-react'
import { PUBLIC_SOLANA_RPC, RPC_ENDPOINT } from './constants'

export type FeeSharePlan = {
  recipientWallet: PublicKey
  /** Basis points of creator fees for recipient (1–5000 = 0.01%–50%). */
  recipientBps: number
}

/**
 * After mint create: createFeeSharingConfig + updateFeeShares.
 * Wallet shareholders only — never Platform.X social PDAs (pump README:
 * only Platform.GitHub is supported for social; X risks lost fees).
 * Split can be set once; call only when recipient wallet is known.
 */
export async function applyWalletFeeShares(
  connection: Connection,
  wallet: WalletContextState,
  mint: PublicKey,
  plan: FeeSharePlan,
): Promise<string> {
  if (!wallet.publicKey) throw new Error('Connect a Solana wallet first.')
  if (plan.recipientBps < 1 || plan.recipientBps > 5000) {
    throw new Error('Fee share must be 1–5000 bps (0.01%–50%).')
  }
  const creatorBps = 10_000 - plan.recipientBps
  if (creatorBps < 1) throw new Error('Creator must retain at least 1 bps.')
  if (plan.recipientWallet.equals(wallet.publicKey)) {
    throw new Error('Fee share recipient cannot be the creator wallet.')
  }

  const { PUMP_SDK } = await import('@pump-fun/pump-sdk')
  const creator = wallet.publicKey

  const createIx: TransactionInstruction = await PUMP_SDK.createFeeSharingConfig({
    creator,
    mint,
    pool: null,
  })

  const updateIx: TransactionInstruction = await PUMP_SDK.updateFeeShares({
    authority: creator,
    mint,
    currentShareholders: [creator],
    newShareholders: [
      { address: creator, shareBps: creatorBps },
      { address: plan.recipientWallet, shareBps: plan.recipientBps },
    ],
  })

  let active = connection
  let blockhash: string
  let lastValidBlockHeight: number
  try {
    ;({ blockhash, lastValidBlockHeight } =
      await active.getLatestBlockhash('confirmed'))
  } catch {
    if (RPC_ENDPOINT !== PUBLIC_SOLANA_RPC) {
      active = new Connection(PUBLIC_SOLANA_RPC, 'confirmed')
      ;({ blockhash, lastValidBlockHeight } =
        await active.getLatestBlockhash('confirmed'))
    } else {
      throw new Error('RPC failed while fetching blockhash for fee share.')
    }
  }

  const tx = new Transaction({
    feePayer: creator,
    blockhash,
    lastValidBlockHeight,
  }).add(createIx, updateIx)

  let signature: string
  if (wallet.sendTransaction) {
    signature = await wallet.sendTransaction(tx, active, {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
      maxRetries: 3,
    })
  } else if (wallet.signTransaction) {
    const signed = await wallet.signTransaction(tx)
    signature = await active.sendRawTransaction(signed.serialize(), {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
    })
  } else {
    throw new Error('No send/sign method on wallet.')
  }

  await active.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed',
  )
  return signature
}

/** Distribute accrued creator fees to on-chain shareholders (bags-like Claim). */
export async function distributeCreatorFees(
  connection: Connection,
  wallet: WalletContextState,
  mintStr: string,
): Promise<string> {
  if (!wallet.publicKey) throw new Error('Connect a Solana wallet first.')

  const { OnlinePumpSdk } = await import('@pump-fun/pump-sdk')
  const online = new OnlinePumpSdk(connection)
  const mint = new PublicKey(mintStr)
  const { instructions } =
    await online.buildDistributeCreatorFeesInstructions(mint)

  if (!instructions.length) {
    throw new Error('No distribute instructions returned for this mint.')
  }

  let active = connection
  let blockhash: string
  let lastValidBlockHeight: number
  try {
    ;({ blockhash, lastValidBlockHeight } =
      await active.getLatestBlockhash('confirmed'))
  } catch {
    if (RPC_ENDPOINT !== PUBLIC_SOLANA_RPC) {
      active = new Connection(PUBLIC_SOLANA_RPC, 'confirmed')
      ;({ blockhash, lastValidBlockHeight } =
        await active.getLatestBlockhash('confirmed'))
    } else {
      throw new Error('RPC failed while fetching blockhash for claim.')
    }
  }

  const tx = new Transaction({
    feePayer: wallet.publicKey,
    blockhash,
    lastValidBlockHeight,
  }).add(...instructions)

  let signature: string
  if (wallet.sendTransaction) {
    signature = await wallet.sendTransaction(tx, active, {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
      maxRetries: 3,
    })
  } else if (wallet.signTransaction) {
    const signed = await wallet.signTransaction(tx)
    signature = await active.sendRawTransaction(signed.serialize(), {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
    })
  } else {
    throw new Error('No send/sign method on wallet.')
  }

  await active.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed',
  )
  return signature
}

export function normalizeTwitterHandle(raw: string): string | null {
  let h = raw.trim().replace(/^@/, '')
  h = h.replace(/^https?:\/\/(www\.)?(twitter|x)\.com\//i, '')
  h = (h.split(/[/?#]/)[0] ?? '').toLowerCase().trim()
  if (!/^[a-z0-9_]{1,15}$/.test(h)) return null
  return h
}

/** UI percent (0–50) → bps of creator fees for the X recipient. */
export function percentToRecipientBps(percent: number): number {
  return Math.round(Math.max(0, Math.min(50, percent)) * 100)
}
